import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "../../../design-system";
import UploadDownloadArea from "./UploadDownloadArea";

/**
 * Letter Q — "The One."
 *
 * The un-fused original concept, kept deliberately independent of Letter P so the two can be
 * compared: a dark, editor-grade "spreadsheet at night" with its own palette and type, built
 * around ONE signature moment — click a row in the (mocked, self-contained) spreadsheet and the
 * clip physically DROPS onto a timeline track below. The Matrix idea stays a quiet spine, never
 * the face: two doors (let the machine cut it / cut it yourself), and the page lets you feel the
 * difference instead of reading about it — the "machine" door assembles ITS edit in front of you.
 *
 * All product claims trace to ET_BEHAVIOR_SPEC.md; the demo data here is mocked so the
 * interaction is fully controllable (the REAL editor is one click away and says so).
 */

/* ── palette (Q's own — graphite void, hairline grid, one loud cherry) ── */
const VOID = "#0B0D10";
const CELL = "#14181D";
const GRID = "#232A31";
const CHERRY = "#E11D3A";
const GLOW = "#FF456A";
const MACHINE = "#6B7A88";
const BONE = "#E8E6E1";
const DIM = "#96A0AA";

const display = "'Bricolage Grotesque', 'Outfit', sans-serif";
const body = "'IBM Plex Sans', 'Outfit', sans-serif";
const mono = "'IBM Plex Mono', 'SFMono-Regular', Consolas, monospace";

const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@500;700;800&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;600&display=swap";

/* ── mocked transcript: 14 rows of an interview about making things ── */
type MockRow = {
  id: number; speaker: "A" | "B"; sent: "+" | "=" | "−"; sec: number; viral: number;
  filler?: boolean; q?: boolean; text: string;
};
const ROWS: MockRow[] = [
  { id: 1, speaker: "A", sent: "=", sec: 4.2, viral: 41, q: true, text: "So when did you know this was the thing you had to build?" },
  { id: 2, speaker: "B", sent: "+", sec: 6.8, viral: 88, text: "The night I watched four hours of footage to find one sentence I already knew was in there." },
  { id: 3, speaker: "B", sent: "=", sec: 3.1, viral: 35, filler: true, text: "I mean, you know how it is, right, like…" },
  { id: 4, speaker: "B", sent: "+", sec: 5.4, viral: 92, text: "Every sentence should just be a row. Search it. Sort it. Keep the good ones." },
  { id: 5, speaker: "A", sent: "=", sec: 2.9, viral: 22, q: true, text: "And the AI does that for you?" },
  { id: 6, speaker: "B", sent: "−", sec: 7.6, viral: 79, text: "The AI makes ITS version of your edit. Ask it twice, you get two different films — neither one is yours." },
  { id: 7, speaker: "B", sent: "=", sec: 2.2, viral: 18, filler: true, text: "Um, so, yeah, anyway —" },
  { id: 8, speaker: "B", sent: "+", sec: 6.1, viral: 95, text: "So we ask the AI the expensive questions, and leave you the only one that matters: which moments are yours." },
  { id: 9, speaker: "A", sent: "+", sec: 3.8, viral: 54, q: true, text: "That's the cherry-picking part?" },
  { id: 10, speaker: "B", sent: "+", sec: 5.9, viral: 86, text: "Tick the rows in any order — first, that line; then the joke; then the payoff. That IS the edit." },
  { id: 11, speaker: "B", sent: "=", sec: 4.4, viral: 47, text: "Trim the in-point a tenth of a second and the neighbor just gives way. Nothing breaks. Ever." },
  { id: 12, speaker: "A", sent: "=", sec: 3.2, viral: 31, q: true, text: "Where does it end up — a file?" },
  { id: 13, speaker: "B", sent: "+", sec: 5.2, viral: 83, text: "A timeline. It always comes back to the timeline — MP4 if you're done, Premiere XML if you're not." },
  { id: 14, speaker: "B", sent: "+", sec: 4.0, viral: 90, text: "You move the footage in every dimension and nothing gets deleted. In here, you're the one in control." },
];

/* what the "machine door" picks when you let it edit — its version, not yours */
const MACHINE_CUT = [4, 8, 13];

const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.round(s % 60)).padStart(2, "0")}`;
const spk = (s: "A" | "B") => (s === "A" ? "#5B8DB8" : "#B8875B");

/* ══════════════ the mocked spreadsheet + drop-to-timeline (the signature) ══════════════ */
const SpreadsheetDemo: React.FC = () => {
  const [picks, setPicks] = useState<number[]>([]);
  const [dropped, setDropped] = useState<Record<number, boolean>>({});
  const [speakerF, setSpeakerF] = useState<"all" | "A" | "B">("all");
  const [sentF, setSentF] = useState<"all" | "+" | "−">("all");
  const [hideFiller, setHideFiller] = useState(false);
  const [search, setSearch] = useState("");
  const [sortViral, setSortViral] = useState(false);
  const [machineRunning, setMachineRunning] = useState(false);
  const [banner, setBanner] = useState<string>("");
  const timers = useRef<number[]>([]);

  useEffect(() => () => { timers.current.forEach((t) => window.clearTimeout(t)); }, []);

  const visible = useMemo(() => {
    let out = ROWS.filter((r) =>
      (speakerF === "all" || r.speaker === speakerF) &&
      (sentF === "all" || r.sent === sentF) &&
      (!hideFiller || !r.filler) &&
      (!search || r.text.toLowerCase().includes(search.toLowerCase()))
    );
    if (sortViral) out = [...out].sort((a, b) => b.viral - a.viral);
    return out;
  }, [speakerF, sentF, hideFiller, search, sortViral]);

  const toggle = (id: number) => {
    setBanner("");
    setPicks((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
    setDropped((d) => ({ ...d, [id]: false }));
    window.setTimeout(() => setDropped((d) => ({ ...d, [id]: true })), 30);
  };

  /* the machine door: watch the AI assemble ITS cut — then invite yours */
  const runMachine = () => {
    if (machineRunning) return;
    setMachineRunning(true);
    setPicks([]);
    setBanner("Black Cherry is making its version…");
    MACHINE_CUT.forEach((id, i) => {
      timers.current.push(window.setTimeout(() => {
        setPicks((p) => [...p, id]);
        setDropped((d) => ({ ...d, [id]: false }));
        window.setTimeout(() => setDropped((d) => ({ ...d, [id]: true })), 30);
        if (i === MACHINE_CUT.length - 1) {
          setMachineRunning(false);
          setBanner("That's the machine's cut — decent, fast, and not quite yours. Now click the rows YOU would keep.");
        }
      }, 700 * (i + 1)));
    });
  };
  const runHand = () => {
    setPicks([]);
    setBanner("Your turn — click any rows, in the order you want them. That order IS the edit.");
  };

  const pickRows = picks.map((id) => ROWS.find((r) => r.id === id)!).filter(Boolean);
  const totalSec = pickRows.reduce((a, r) => a + r.sec, 0);

  const fchip = (label: string, on: boolean, onClick: () => void) => (
    <Box component="button" onClick={onClick}
      sx={{ cursor: "pointer", px: "9px", py: "4px", borderRadius: "4px", border: `1px solid ${on ? CHERRY : GRID}`, background: on ? "rgba(225,29,58,0.14)" : "transparent", color: on ? "#FFB3C0" : DIM, fontFamily: mono, fontSize: "10.5px", lineHeight: 1, whiteSpace: "nowrap", "&:hover": { borderColor: on ? CHERRY : MACHINE }, "&:focus-visible": { outline: `2px solid ${CHERRY}`, outlineOffset: "2px" } }}>
      {label}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <style>{`
        @keyframes qDrop { 0% { transform: translateY(-46px) scale(0.9); opacity: 0; } 60% { transform: translateY(3px) scale(1.01); opacity: 1; } 100% { transform: translateY(0) scale(1); opacity: 1; } }
        @keyframes qHead { from { left: 0%; } to { left: 100%; } }
        .q-search::placeholder { color: #5A646E; }
        .q-search:focus { border-color: ${CHERRY} !important; }
      `}</style>

      {/* two doors — the page's thesis as buttons */}
      <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap", mb: "14px" }}>
        <Box component="button" onClick={runMachine} aria-disabled={machineRunning}
          sx={{ cursor: machineRunning ? "wait" : "pointer", flex: { xs: "1 1 100%", sm: "0 1 auto" }, px: "18px", py: "11px", borderRadius: "6px", border: `1px solid ${MACHINE}`, background: "rgba(107,122,136,0.12)", color: "#C7D1DA", fontFamily: body, fontWeight: 600, fontSize: "14px", opacity: machineRunning ? 0.6 : 1, "&:hover": { background: "rgba(107,122,136,0.2)" }, "&:focus-visible": { outline: `2px solid ${MACHINE}`, outlineOffset: "2px" } }}>
          Let the machine cut it
        </Box>
        <Box component="button" onClick={runHand}
          sx={{ cursor: "pointer", flex: { xs: "1 1 100%", sm: "0 1 auto" }, px: "18px", py: "11px", borderRadius: "6px", border: "none", background: CHERRY, color: "#fff", fontFamily: body, fontWeight: 600, fontSize: "14px", boxShadow: "0 8px 26px -10px rgba(225,29,58,0.7)", "&:hover": { background: "#C81834" }, "&:focus-visible": { outline: `2px solid ${BONE}`, outlineOffset: "2px" } }}>
          🍒 Cut it yourself
        </Box>
        {banner && (
          <Typography sx={{ alignSelf: "center", fontFamily: body, fontSize: "13px", color: banner.startsWith("Black") ? "#C7D1DA" : "#FFB3C0" }}>{banner}</Typography>
        )}
      </Box>

      {/* the sheet */}
      <Box sx={{ border: `1px solid ${GRID}`, borderRadius: "8px 8px 0 0", overflow: "hidden", background: CELL }}>
        {/* toolbar = the dimensions, live */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", px: "10px", py: "8px", borderBottom: `1px solid ${GRID}`, background: "rgba(255,255,255,0.015)" }}>
          <input className="q-search" placeholder="search the footage…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ background: VOID, border: `1px solid ${GRID}`, borderRadius: "4px", color: BONE, fontFamily: mono, fontSize: "11px", padding: "5px 8px", width: "150px", outline: "none" }} />
          {fchip("spkr A", speakerF === "A", () => setSpeakerF(speakerF === "A" ? "all" : "A"))}
          {fchip("spkr B", speakerF === "B", () => setSpeakerF(speakerF === "B" ? "all" : "B"))}
          {fchip("+ only", sentF === "+", () => setSentF(sentF === "+" ? "all" : "+"))}
          {fchip("− only", sentF === "−", () => setSentF(sentF === "−" ? "all" : "−"))}
          {fchip("hide filler", hideFiller, () => setHideFiller(!hideFiller))}
          {fchip("sort by viral ↓", sortViral, () => setSortViral(!sortViral))}
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontFamily: mono, fontSize: "10.5px", color: DIM }}>{visible.length}/{ROWS.length} rows</Typography>
        </Box>

        {/* header row */}
        <Box sx={{ display: "grid", gridTemplateColumns: "34px 44px 30px 1fr 52px 46px", gap: 0, borderBottom: `1px solid ${GRID}` }}>
          {["", "SPKR", "±", "TRANSCRIPT — click a row to drop it on the timeline", "SEC", "VIRAL"].map((h, i) => (
            <Typography key={i} sx={{ fontFamily: mono, fontSize: "9px", fontWeight: 600, letterSpacing: "0.08em", color: "#5A646E", px: "8px", py: "6px", borderRight: i < 5 ? `1px solid ${GRID}` : "none", whiteSpace: "nowrap", overflow: "hidden" }}>{h}</Typography>
          ))}
        </Box>

        {/* rows */}
        <Box sx={{ maxHeight: "338px", overflowY: "auto" }}>
          {visible.map((r) => {
            const on = picks.includes(r.id);
            const ord = picks.indexOf(r.id) + 1;
            return (
              <Box key={r.id} component="button" onClick={() => toggle(r.id)}
                sx={{
                  display: "grid", gridTemplateColumns: "34px 44px 30px 1fr 52px 46px", width: "100%", textAlign: "left",
                  background: on ? "rgba(225,29,58,0.09)" : "transparent", border: "none", borderBottom: `1px solid ${GRID}`,
                  cursor: "pointer", p: 0, opacity: r.filler && !on ? 0.45 : 1,
                  "&:hover": { background: on ? "rgba(225,29,58,0.13)" : "rgba(255,255,255,0.03)" },
                  "&:focus-visible": { outline: `2px solid ${CHERRY}`, outlineOffset: "-2px" },
                }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", borderRight: `1px solid ${GRID}`, py: "7px" }}>
                  <Box sx={{ width: 15, height: 15, borderRadius: "3px", border: `1.5px solid ${on ? CHERRY : "#3A434C"}`, background: on ? CHERRY : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {on && <Typography sx={{ fontFamily: mono, fontSize: "8.5px", fontWeight: 700, color: "#fff", lineHeight: 1 }}>{ord}</Typography>}
                  </Box>
                </Box>
                <Typography sx={{ fontFamily: mono, fontSize: "10.5px", color: spk(r.speaker), px: "8px", py: "8px", borderRight: `1px solid ${GRID}` }}>{r.speaker}</Typography>
                <Typography sx={{ fontFamily: mono, fontSize: "11px", color: r.sent === "+" ? "#5FBF8A" : r.sent === "−" ? GLOW : DIM, px: "8px", py: "8px", borderRight: `1px solid ${GRID}`, textAlign: "center" }}>{r.sent}</Typography>
                <Typography sx={{ fontFamily: body, fontSize: "12.5px", lineHeight: 1.45, color: on ? BONE : "#C3CBD2", px: "10px", py: "7px", borderRight: `1px solid ${GRID}` }}>
                  {r.text}{r.filler && <Box component="span" sx={{ fontFamily: mono, fontSize: "9px", color: "#5A646E", ml: "6px" }}>[filler]</Box>}
                </Typography>
                <Typography sx={{ fontFamily: mono, fontSize: "10.5px", color: DIM, px: "8px", py: "8px", borderRight: `1px solid ${GRID}`, textAlign: "right" }}>{r.sec.toFixed(1)}</Typography>
                <Typography sx={{ fontFamily: mono, fontSize: "10.5px", fontWeight: 600, color: r.viral >= 80 ? GLOW : DIM, px: "8px", py: "8px", textAlign: "right" }}>{r.viral}</Typography>
              </Box>
            );
          })}
          {!visible.length && (
            <Typography sx={{ fontFamily: body, fontSize: "12.5px", color: DIM, px: "12px", py: "16px" }}>
              No rows match — loosen a filter. (The footage is still all there; filters never delete.)
            </Typography>
          )}
        </Box>
      </Box>

      {/* the seam */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", py: "5px", borderLeft: `1px solid ${GRID}`, borderRight: `1px solid ${GRID}`, background: "rgba(225,29,58,0.05)" }}>
        <Typography sx={{ fontFamily: mono, fontSize: "9.5px", letterSpacing: "0.1em", color: "#8A5560" }}>▼ ▼ ▼</Typography>
      </Box>

      {/* the timeline — where dropped clips land */}
      <Box sx={{ border: `1px solid ${GRID}`, borderRadius: "0 0 8px 8px", background: "#0E1114", p: "10px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "7px" }}>
          <Typography sx={{ fontFamily: mono, fontSize: "10px", fontWeight: 600, letterSpacing: "0.08em", color: DIM }}>TIMELINE</Typography>
          <Typography sx={{ fontFamily: mono, fontSize: "10px", color: "#5A646E" }}>
            {pickRows.length ? `${pickRows.length} clips · ${fmt(totalSec)} · exports in this order` : "empty — your cut appears here"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: "8px", alignItems: "stretch" }}>
          <Box sx={{ flex: "0 0 30px", display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${GRID}`, borderRadius: "4px" }}>
            <Typography sx={{ fontFamily: mono, fontSize: "9.5px", color: DIM }}>V1</Typography>
          </Box>
          <Box sx={{ position: "relative", flex: 1, minWidth: 0, minHeight: 54, display: "flex", gap: "3px", border: `1px solid ${GRID}`, borderRadius: "4px", p: "3px", overflow: "hidden", background: "repeating-linear-gradient(90deg, transparent 0 59px, rgba(255,255,255,0.025) 59px 60px)" }}>
            {pickRows.map((r) => (
              <Box key={r.id} onClick={() => toggle(r.id)} title={`${r.text} (click to remove)`}
                sx={{
                  flex: `${r.sec} 1 0`, minWidth: "34px", borderRadius: "3px", cursor: "pointer", overflow: "hidden",
                  border: `1px solid ${spk(r.speaker)}88`, background: `linear-gradient(180deg, ${spk(r.speaker)}44, ${spk(r.speaker)}1A)`,
                  animation: dropped[r.id] ? "qDrop .38s cubic-bezier(.2,.9,.3,1.2)" : "none",
                  display: "flex", flexDirection: "column", justifyContent: "space-between", p: "4px",
                  "&:hover": { borderColor: GLOW },
                  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                }}>
                <Typography sx={{ fontFamily: mono, fontSize: "8.5px", color: "#fff", opacity: 0.85 }}>#{picks.indexOf(r.id) + 1}</Typography>
                <Typography noWrap sx={{ fontFamily: body, fontSize: "9px", color: BONE, opacity: 0.9 }}>{r.text}</Typography>
              </Box>
            ))}
            {pickRows.length > 0 && (
              <Box aria-hidden sx={{ position: "absolute", top: 0, bottom: 0, width: "2px", background: GLOW, boxShadow: `0 0 8px ${GLOW}`, animation: `qHead ${Math.max(6, totalSec)}s linear infinite`, pointerEvents: "none", "@media (prefers-reduced-motion: reduce)": { animation: "none" } }} />
            )}
          </Box>
        </Box>
      </Box>

      <Typography sx={{ fontFamily: body, fontSize: "12px", color: "#5A646E", mt: "8px" }}>
        This demo is staged so you can feel the idea in ten seconds — the real editor does all of this
        (and eleven more dimensions) on your actual footage.
      </Typography>
    </Box>
  );
};

/* ══════════════ quiet Matrix spine — the essay, one section, no costume ══════════════ */
const Choice: React.FC = () => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 0, border: `1px solid ${GRID}`, borderRadius: "8px", overflow: "hidden" }}>
    <Box sx={{ p: { xs: "22px", md: "34px" }, background: "rgba(107,122,136,0.07)", borderRight: { md: `1px solid ${GRID}` }, borderBottom: { xs: `1px solid ${GRID}`, md: "none" } }}>
      <Typography sx={{ fontFamily: mono, fontSize: "10px", letterSpacing: "0.14em", color: MACHINE, mb: "12px" }}>WHEN THE TOOL USES YOU</Typography>
      <Typography sx={{ fontFamily: display, fontWeight: 700, fontSize: { xs: "20px", md: "24px" }, color: "#C7D1DA", lineHeight: 1.25, mb: "12px" }}>
        Ask an AI for the film in your head, and you get the film in its head.
      </Typography>
      <Typography sx={{ fontFamily: body, fontSize: "14px", lineHeight: 1.7, color: "#8D99A4" }}>
        The longer the prompt, the deeper the idea, the more ways there are to read it — grammar slips,
        no intonation, no shared memory. Any of a thousand valid edits comes back, and none of them is the
        one you meant. That's not a flaw to patch. Words alone can't carry a vision.
      </Typography>
    </Box>
    <Box sx={{ p: { xs: "22px", md: "34px" }, background: "rgba(225,29,58,0.05)" }}>
      <Typography sx={{ fontFamily: mono, fontSize: "10px", letterSpacing: "0.14em", color: CHERRY, mb: "12px" }}>WHEN YOU USE THE TOOL</Typography>
      <Typography sx={{ fontFamily: display, fontWeight: 700, fontSize: { xs: "20px", md: "24px" }, color: BONE, lineHeight: 1.25, mb: "12px" }}>
        In here, you move the footage in every dimension — and nothing breaks.
      </Typography>
      <Typography sx={{ fontFamily: body, fontSize: "14px", lineHeight: 1.7, color: "#C9B8BC" }}>
        Filter, re-segment, reorder, trim — every move is reversible, nothing is deleted, the transcript
        never lies to you. The machine did the labor: every word transcribed, every speaker tagged, every
        clip scored. The judgment — which moments, in which order — stays where it belongs. With you.
      </Typography>
    </Box>
  </Box>
);

/* ══════════════ page ══════════════ */
const TheOnePage: React.FC = () => {
  useEffect(() => {
    if (document.querySelector(`link[href="${FONT_HREF}"]`)) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONT_HREF;
    document.head.appendChild(l);
  }, []);

  return (
    <Box sx={{ background: `linear-gradient(180deg, ${VOID} 0%, #0D1013 100%)`, minHeight: "100%", pb: "72px" }}>
      <Box sx={{ width: "100%", maxWidth: "1180px", margin: "0 auto", px: { xs: "16px", md: "32px" }, pt: { xs: "26px", md: "48px" } }}>

        {/* hero — a claim and the thing itself */}
        <Box sx={{ mb: { xs: "26px", md: "38px" }, maxWidth: "860px" }}>
          <Typography sx={{ fontFamily: mono, fontSize: "11px", letterSpacing: "0.14em", color: DIM, mb: "14px" }}>
            CHERRYCLIPZ — A SPREADSHEET FOR VIDEO
          </Typography>
          <Typography sx={{ fontFamily: display, fontWeight: 800, fontSize: { xs: "38px", sm: "52px", md: "64px" }, lineHeight: 1.0, letterSpacing: "-0.02em", color: BONE, mb: "16px" }}>
            Your video is a spreadsheet.<br />
            <Box component="span" sx={{ color: CHERRY }}>You</Box> are the editor it obeys.
          </Typography>
          <Typography sx={{ fontFamily: body, fontSize: { xs: "15px", md: "17px" }, lineHeight: 1.6, color: DIM, maxWidth: "640px" }}>
            Every sentence becomes a row — speaker, emotion, seconds, virality. Below is the whole idea in
            one surface: pick rows up top, watch your cut assemble on the timeline. Try both doors.
          </Typography>
        </Box>

        {/* THE signature surface */}
        <SpreadsheetDemo />

        {/* the choice — quiet spine */}
        <Box sx={{ mt: { xs: "56px", md: "88px" } }}>
          <Typography sx={{ fontFamily: mono, fontSize: "10px", letterSpacing: "0.14em", color: "#5A646E", mb: "16px" }}>ROW 2 — WHY A HAND BEATS A PROMPT</Typography>
          <Choice />
        </Box>

        {/* dimensions, compressed to a manifest */}
        <Box sx={{ mt: { xs: "56px", md: "88px" } }}>
          <Typography sx={{ fontFamily: mono, fontSize: "10px", letterSpacing: "0.14em", color: "#5A646E", mb: "16px" }}>ROW 3 — THE DIMENSIONS</Typography>
          <Typography sx={{ fontFamily: display, fontWeight: 700, fontSize: { xs: "24px", md: "32px" }, color: BONE, letterSpacing: "-0.01em", mb: "18px", maxWidth: "760px" }}>
            Twelve axes. One sheet. Astronomically many cuts — and filters that collapse them to yours.
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: "1px", background: GRID, border: `1px solid ${GRID}`, borderRadius: "8px", overflow: "hidden" }}>
            {[
              ["search", "a word → its exact clips"],
              ["speaker", "any combination of voices"],
              ["emotion", "positive / neutral / negative"],
              ["time + length", "3.0–9.0s, ≤120 chars — done"],
              ["segment by", "sentences · words · N-second chunks"],
              ["virality", "AI-scored 0–100, sortable"],
              ["filler", "dim it, hide it, or review only it"],
              ["silences", "the gaps are rows too"],
              ["pen highlight", "export just the marked phrase"],
              ["trim ±0.1s", "neighbors give way, nothing breaks"],
              ["pick order", "the sequence is the edit"],
              ["translate", "pick in any of 47 languages"],
            ].map(([n, d]) => (
              <Box key={n} sx={{ background: CELL, p: "14px 16px" }}>
                <Typography sx={{ fontFamily: mono, fontSize: "11px", fontWeight: 600, color: "#FFB3C0", mb: "4px" }}>{n}</Typography>
                <Typography sx={{ fontFamily: body, fontSize: "12.5px", lineHeight: 1.5, color: DIM }}>{d}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* the timeline thesis */}
        <Box sx={{ mt: { xs: "56px", md: "88px" }, maxWidth: "820px" }}>
          <Typography sx={{ fontFamily: mono, fontSize: "10px", letterSpacing: "0.14em", color: "#5A646E", mb: "16px" }}>ROW 4 — IT ALL COMES BACK TO THE TIMELINE</Typography>
          <Typography sx={{ fontFamily: display, fontWeight: 700, fontSize: { xs: "24px", md: "32px" }, color: BONE, letterSpacing: "-0.01em", mb: "14px" }}>
            Rough-cut in the sheet. Finish wherever you're fastest.
          </Typography>
          <Typography sx={{ fontFamily: body, fontSize: "14.5px", lineHeight: 1.7, color: DIM }}>
            Export MP4 when you're done — or EDL / Premiere XML when you're not, and your picks open as a
            sequence in Premiere for the fine trims. Next on the road: the sheet as a panel inside Premiere
            itself — tick clips, they land on the real timeline. Editing stays your craft; finding stops
            being your job.
          </Typography>
        </Box>

        {/* CTA */}
        <Box sx={{ mt: { xs: "56px", md: "88px" }, display: "flex", flexDirection: "column", gap: "12px", alignItems: "center" }}>
          <Typography sx={{ fontFamily: display, fontWeight: 800, fontSize: { xs: "26px", md: "34px" }, color: BONE, textAlign: "center", letterSpacing: "-0.01em" }}>
            The demo was staged. <Box component="span" sx={{ color: CHERRY }}>This part isn't.</Box>
          </Typography>
          <Box id="cc-demo-upload" sx={{ width: "100%", maxWidth: "760px" }}><UploadDownloadArea /></Box>
          <Typography sx={{ fontFamily: body, fontSize: "13px", color: "#5A646E", textAlign: "center" }}>
            Upload a video or paste a link — it comes back as a spreadsheet of clips. Longer videos take a little longer.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TheOnePage;
